/**
 * Phase 3.28 — SocialMediaExportSection.
 * Master-Toggle + per-Channel CheckRow + Credentials-Panel pro Plattform.
 */
import { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { CheckRow } from "./_shared";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const SOCIAL_CHANNELS = [
    // Gängigste Kanäle zuerst (User-Wunsch Juni 2026)
    { key: "whatsapp", label: "WhatsApp", hint: "Inserate als Status posten / Broadcast-Liste." },
    { key: "instagram", label: "Instagram", hint: "Reels & Posts auf Ihrem Business-Account." },
    { key: "tiktok", label: "TikTok", hint: "Kurzvideos & Slideshows für Ihr Sortiment." },
    { key: "facebook", label: "Facebook", hint: "Posts auf Ihrer Händler-Page + Marketplace." },
    { key: "youtube", label: "YouTube", hint: "Video-Walkarounds & Shorts auf Ihrem Channel." },
    { key: "x", label: "X (Twitter)", hint: "Posts & Threads zu Inseraten und Aktionen." },
    // Weitere Kanäle
    { key: "whatsapp_status", label: "WhatsApp Status", hint: "Status-Beitrag (24 h) auf Ihrem Business-Account." },
    { key: "linkedin", label: "LinkedIn", hint: "Business-Posts auf Ihrer Firmen-Seite." },
    { key: "forums", label: "Chats / Foren", hint: "Posts in Motorrad-Foren & Community-Chats (z. B. Motor-Talk, Reddit r/Motorcycles)." },
];

export function SocialMediaExportSection({ currentUser, hideMasterToggle = false, inlineCredentials = false }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState({});
    // Local optimistic mirror of enabled/mode so the channel panel + radio
    // selection reflect the user's click immediately, before the PATCH
    // round-trip + auth refresh complete. Keyed by `social_<channel>_<field>`.
    const [local, setLocal] = useState({});

    // Sync local draft + mirror with currentUser when it changes
    // (initial load, post-refresh, role change).
    useEffect(() => {
        const next = {};
        for (const c of SOCIAL_CHANNELS) {
            next[`social_${c.key}_username`] = currentUser?.[`social_${c.key}_username`] || "";
            next[`social_${c.key}_token`] = currentUser?.[`social_${c.key}_token`] || "";
            next[`social_${c.key}_note`] = currentUser?.[`social_${c.key}_note`] || "";
        }
        setDraft(next);
        const nextLocal = {};
        for (const c of SOCIAL_CHANNELS) {
            nextLocal[`social_${c.key}_enabled`] = !!currentUser?.[`social_${c.key}_enabled`];
            nextLocal[`social_${c.key}_mode`] = currentUser?.[`social_${c.key}_mode`] || "shared";
        }
        setLocal(nextLocal);
    }, [currentUser]);

    // Persist a single field. Optimistically updates the local mirror first
    // so the UI reacts on the same React tick, then refreshes auth state.
    const patch = async (payload) => {
        setLocal((p) => ({ ...p, ...payload }));
        setBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", payload);
            await refresh();
            toast.success("Gespeichert");
        } catch (e) {
            // Roll back the optimistic update so the UI matches reality.
            setLocal((p) => {
                const rolled = { ...p };
                for (const k of Object.keys(payload)) {
                    rolled[k] = currentUser?.[k] ?? (k.endsWith("_mode") ? "shared" : false);
                }
                return rolled;
            });
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const saveCredentials = async (channelKey) => {
        const payload = {
            [`social_${channelKey}_username`]: draft[`social_${channelKey}_username`] || null,
            [`social_${channelKey}_token`]: draft[`social_${channelKey}_token`] || null,
            [`social_${channelKey}_note`]: draft[`social_${channelKey}_note`] || null,
        };
        await patch(payload);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="social-media-section">
            <div className="flex items-center gap-2 mb-1">
                <Share2 className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-lg">Sozialmedia</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Veröffentlichen Sie Ihre Inserate automatisch auf den ausgewählten Plattformen.
                Pro Kanal können Sie wählen, ob unser <strong>geteilter „Wir Die Zweiradhändler"-Zugang</strong>
                oder Ihr <strong>eigener Händler-Zugang</strong> genutzt werden soll.
            </p>

            {/* Master-Toggle — Phase 3.19. Kanäle werden erst nach dem Aktivieren sichtbar. */}
            {!hideMasterToggle && (
                <CheckRow
                    label="Sozialmedia-Veröffentlichung aktivieren"
                    hint="Erst nach Aktivierung erscheinen die Kanal-Optionen unten."
                    checked={!!currentUser?.social_media_enabled}
                    disabled={busy}
                    onChange={(v) => patch({ social_media_enabled: v })}
                    testId="social-media-master-checkbox"
                />
            )}

            {!hideMasterToggle && !currentUser?.social_media_enabled ? (
                <div className="mt-3 text-[11px] text-gray-500 italic" data-testid="social-media-disabled-hint">
                    Bitte aktivieren, um WhatsApp / WhatsApp Status / Instagram / TikTok / Facebook / YouTube / LinkedIn / X / Chats &amp; Foren zu konfigurieren.
                </div>
            ) : (
                <>
                    <div className="my-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-sm">
                        ℹ️ Aktuell nur Vormerkung — die technische Anbindung wird in Kürze freigeschaltet.
                        Ihre Einstellungen sind gespeichert und werden beim Go-Live übernommen.
                    </div>
                    <div className="space-y-3" data-testid="social-channels-list">
                        {SOCIAL_CHANNELS.map((c) => {
                            const enabled = !!local[`social_${c.key}_enabled`];
                            const mode = local[`social_${c.key}_mode`] || "shared";
                            // "Ich habe keinen Kanal" — dealer signals they have no presence
                            // on this platform. Disables the credentials block and prevents
                            // the platform from nagging them later. Stored per channel.
                            const noChannel = !!currentUser?.[`social_${c.key}_no_channel`];
                            return (
                        <div key={c.key} className="border border-gray-200 rounded-sm" data-testid={`social-channel-${c.key}`}>
                            <CheckRow
                                label={c.label}
                                hint={c.hint}
                                checked={enabled && !noChannel}
                                disabled={busy || noChannel}
                                onChange={(v) => patch({
                                    [`social_${c.key}_enabled`]: v,
                                    // initialise mode to "shared" on first enable
                                    ...(v && !local[`social_${c.key}_mode`] ? { [`social_${c.key}_mode`]: "shared" } : {}),
                                })}
                                testId={`social-${c.key}-checkbox`}
                            />
                            <div className="px-5 pb-3" data-testid={`social-${c.key}-no-channel-row`}>
                                <label className="inline-flex items-center gap-2 cursor-pointer text-[11px] text-gray-600">
                                    <input
                                        type="checkbox"
                                        checked={noChannel}
                                        disabled={busy}
                                        onChange={(e) => patch({
                                            [`social_${c.key}_no_channel`]: e.target.checked,
                                            // turning "no channel" on also disables the channel itself
                                            ...(e.target.checked ? { [`social_${c.key}_enabled`]: false } : {}),
                                        })}
                                        className="w-3.5 h-3.5 accent-[#111827]"
                                        data-testid={`social-${c.key}-no-channel`}
                                    />
                                    Ich habe keinen {c.label}-Kanal
                                </label>
                            </div>
                            {enabled && !noChannel && (
                                <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50" data-testid={`social-${c.key}-panel`}>
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                                        Welcher Zugang soll genutzt werden? (mehrfach auswählbar)
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                        {(() => {
                                            const useShared = mode === "shared" || mode === "both";
                                            const useOwn = mode === "own" || mode === "both";
                                            const computeMode = (s, o) => (s && o ? "both" : s ? "shared" : o ? "own" : null);
                                            return (
                                                <>
                                                    <label className={`flex items-start gap-2 p-3 rounded-sm border cursor-pointer ${useShared ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 bg-white hover:border-gray-400"}`} data-testid={`social-${c.key}-mode-shared`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={useShared}
                                                            disabled={busy}
                                                            onChange={(e) => patch({ [`social_${c.key}_mode`]: computeMode(e.target.checked, useOwn) || "shared" })}
                                                            className="mt-0.5 accent-[#FFCC00]"
                                                        />
                                                        <div className="text-xs">
                                                            <div className="font-bold text-[#111827]">„Wir Die Zweiradhändler"-Zugang nutzen</div>
                                                            <div className="text-gray-600 mt-0.5">Veröffentlichung über unseren gemeinsamen Kanal — kein eigenes Login nötig.</div>
                                                        </div>
                                                    </label>
                                                    <label className={`flex items-start gap-2 p-3 rounded-sm border cursor-pointer ${useOwn ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 bg-white hover:border-gray-400"}`} data-testid={`social-${c.key}-mode-own`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={useOwn}
                                                            disabled={busy}
                                                            onChange={(e) => patch({ [`social_${c.key}_mode`]: computeMode(useShared, e.target.checked) || "own" })}
                                                            className="mt-0.5 accent-[#FFCC00]"
                                                        />
                                                        <div className="text-xs">
                                                            <div className="font-bold text-[#111827]">Eigenen {c.label}-Zugang nutzen</div>
                                                            <div className="text-gray-600 mt-0.5">Wir posten unter Ihrem eigenen Profil — Benutzerdaten unten erfassen.</div>
                                                        </div>
                                                    </label>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    {(inlineCredentials || mode === "own" || mode === "both") && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`social-${c.key}-credentials`}>
                                            <div>
                                                <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Benutzername / Handle</Label>
                                                <Input
                                                    value={draft[`social_${c.key}_username`] || ""}
                                                    onChange={(e) => setDraft((p) => ({ ...p, [`social_${c.key}_username`]: e.target.value }))}
                                                    placeholder={c.key === "whatsapp" ? "+49 …" : `@ihr_${c.key}`}
                                                    className="rounded-sm mt-1 text-sm"
                                                    disabled={busy}
                                                    data-testid={`social-${c.key}-username`}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Token / Passwort</Label>
                                                <Input
                                                    type="password"
                                                    value={draft[`social_${c.key}_token`] || ""}
                                                    onChange={(e) => setDraft((p) => ({ ...p, [`social_${c.key}_token`]: e.target.value }))}
                                                    placeholder="••••••••"
                                                    className="rounded-sm mt-1 text-sm font-mono-num"
                                                    disabled={busy}
                                                    data-testid={`social-${c.key}-token`}
                                                />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Notiz (optional)</Label>
                                                <Input
                                                    value={draft[`social_${c.key}_note`] || ""}
                                                    onChange={(e) => setDraft((p) => ({ ...p, [`social_${c.key}_note`]: e.target.value }))}
                                                    placeholder="z. B. nur Stories, keine Reels"
                                                    className="rounded-sm mt-1 text-sm"
                                                    disabled={busy}
                                                    data-testid={`social-${c.key}-note`}
                                                />
                                            </div>
                                            <div className="sm:col-span-2 flex justify-end">
                                                <Button
                                                    onClick={() => saveCredentials(c.key)}
                                                    disabled={busy}
                                                    size="sm"
                                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm"
                                                    data-testid={`social-${c.key}-save`}
                                                >
                                                    Zugangsdaten speichern
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
                </>
            )}
        </div>
    );
}


// ============================================================================
// MARKETING — bundles Börsenexporte + Sozialmedia + Zielgruppen under one
// section, with a prominent transaction-code header (H_<customer_number>)
// and revenue/points KPIs. Phase 3.20.
// QR-Code (Phase 3.22) lets dealers print/share the transaction code easily.
// ============================================================================
// Phase 3.26 — kleines KPI-Card-Subcomponent für Attribution-Buckets.
// Phase 3.27 — MarketingSection + AttrCard in /components/dealer/MarketingSection.jsx ausgelagert.
export { MarketingSection } from "./MarketingSection";


// ============================================================================
// Phase 3.25 — Werbemittel & Tracking
// 4 Toggles (Banner / Landing / QR-Smiley / B2C-Ads) + Previews + Downloads.
// Backend liefert SVG-Banner und HTML-Landingpage dynamisch mit eingebettetem
// Transaktionscode für serverseitiges Cookie-Attribution-Tracking.
// ============================================================================
// Phase 3.27 — WerbemittelSection in /components/dealer/WerbemittelSection.jsx ausgelagert.
export { WerbemittelSection } from "./WerbemittelSection";

